import { ArrowPathIcon, DocumentDuplicateIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from '@nextui-org/react';
import { ColumnSize } from '@react-types/table';
import React, { ReactNode, useRef, useEffect } from 'react';

import { servers } from '@common/tsrest';
import { ServerSchema, ServerUUID, MonitorStatus, ClusterTag } from '@common/types';
import { ConfirmationUi } from '@portal/components/common/Confirmation.ui';
import { IconTooltipPopoverUi } from '@portal/components/common/IconTooltipPopover.ui';
import { cn, copyToClipboard } from '@portal/utils';

// Detect Safari browser
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export type Data = {
  serverName: string;
  serverHost: string;
  connectionType: string | ReactNode;
  clusterTag: string;
  ramUsage: string | ReactNode;
  networkSpeed: string | ReactNode;
  networkUsageToday: string | ReactNode;
  networkUsageYesterday: string | ReactNode;
  uptime: string | ReactNode;
  service: string | ReactNode;
  status: string | ReactNode;
  schema: ServerSchema;
};

type Columns = {
  key: string;
  label: string;
  width?: ColumnSize;
};
const getColumns = (showSslPasswords: boolean): Array<Columns> => {
  const baseColumns: Array<Columns> = [
    {
      key: 'server-name',
      label: 'Server Name',
    },
    {
      key: 'server-host',
      label: 'Server Host',
      width: 180,
    },
    {
      key: 'cluster-tag',
      label: 'Cluster Tag',
      width: 120,
    },
    {
      key: 'ram-usage',
      label: 'RAM Usage',
      width: 220,
    },
    {
      key: 'speed',
      label: 'Speed',
      width: 100,
    },
    {
      key: 'today',
      label: 'Today',
      width: 112,
    },
    {
      key: 'yesterday',
      label: 'Yesterday',
      width: 112,
    },
    {
      key: 'uptime',
      label: 'Uptime',
      width: 120,
    },
    {
      key: 'service',
      label: 'Service',
      width: 102,
    },
    {
      key: 'status',
      label: 'Status',
      width: 150,
    },
  ];

  if (showSslPasswords) {
    baseColumns.push({
      key: 'ssl-password',
      label: 'SSL Password',
      width: 150,
    });
  }

  return baseColumns;
};

export const ServerStatsTableUI = ({
  paginatedData,
  tableStatus,
  showSslPasswords,
  sslPasswords,
  sslPasswordLoading,
  sslPasswordErrors,
  onFetchSslPassword,
  selectedCluster,
  serverStats,
}: {
  paginatedData: Data[];
  tableStatus: ReactNode;
  showSslPasswords?: boolean;
  sslPasswords?: Record<ServerUUID, string | null>;
  sslPasswordLoading?: Record<ServerUUID, boolean>;
  sslPasswordErrors?: Record<ServerUUID, string>;
  onFetchSslPassword?: (serverUUID: ServerUUID, serverClusterTag: ClusterTag) => Promise<void>;
  selectedCluster?: string;
  serverStats?: { [key: ServerUUID]: MonitorStatus };
}) => {
  const [isNameCopy, setIsNameCopy] = React.useState('');
  const [nameHover, setNameHover] = React.useState('');

  const [isHostCopy, setIsHostCopy] = React.useState('');
  const [hostHover, setHostHover] = React.useState('');

  const [hoveredRow, setHoveredRow] = React.useState<number | null>(null);

  // Track which servers we've initiated fetching for
  const fetchInitiatedRef = useRef<Set<ServerUUID>>(new Set());
  const prevClusterRef = useRef<string | undefined>(selectedCluster);

  // Reset fetch tracking when showSslPasswords or selectedCluster changes
  useEffect(() => {
    if (!showSslPasswords) {
      fetchInitiatedRef.current.clear();
    } else if (prevClusterRef.current !== selectedCluster) {
      // Clear ref when cluster changes to allow re-fetching for new cluster
      fetchInitiatedRef.current.clear();
      prevClusterRef.current = selectedCluster;
    }
  }, [showSslPasswords, selectedCluster]);

  const onCopyName = async (serverName: string) => {
    if (!(await copyToClipboard(serverName))) return;
    setIsNameCopy(serverName);
    setTimeout(() => setIsNameCopy(''), 1000);
  };

  const onCopyHost = async (serverHost: string) => {
    if (!(await copyToClipboard(serverHost))) return;
    setIsHostCopy(serverHost);
    setTimeout(() => setIsHostCopy(''), 1000);
  };

  const columns = getColumns(showSslPasswords || false);

  return (
    <Table removeWrapper isStriped isHeaderSticky aria-label='registered servers list'>
      <TableHeader columns={columns}>
        {column => (
          <TableColumn key={column.key} width={column.width || null}>
            {column.label}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody emptyContent={tableStatus}>
        {paginatedData.map((data, index) => {
          const isHovered = hoveredRow === index;

          // Fetch SSL password on-demand when column is visible
          if (showSslPasswords && onFetchSslPassword && !fetchInitiatedRef.current.has(data.schema.uuid)) {
            fetchInitiatedRef.current.add(data.schema.uuid);
            onFetchSslPassword(data.schema.uuid, data.schema.clusterTag).catch(err => {
              console.error(`Error fetching SSL password for ${data.schema.uuid}:`, err);
            });
          }

          // Prepare styles based on browser and hover state
          const rowClassName = isSafari
            ? ''
            : 'group hover:shadow-[0_0_0_2px_rgba(59,130,246,0.8),0_20px_25px_-5px_rgba(59,130,246,0.15),0_10px_10px_-5px_rgba(59,130,246,0.1)] hover:rounded-lg';

          // For Safari: use box-shadow to create border effect (borders can't be rounded on table cells)
          const baseCellShadow = isHovered
            ? 'inset 0 2px 0 0 rgba(59, 130, 246, 0.8), inset 0 -2px 0 0 rgba(59, 130, 246, 0.8), 0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            : 'inset 0 2px 0 0 transparent, inset 0 -2px 0 0 transparent';

          const firstCellShadow = isHovered
            ? 'inset 2px 2px 0 0 rgba(59, 130, 246, 0.8), inset 0 -2px 0 0 rgba(59, 130, 246, 0.8), 0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            : 'inset 2px 2px 0 0 transparent, inset 0 -2px 0 0 transparent';

          const lastCellShadow = isHovered
            ? 'inset -2px 2px 0 0 rgba(59, 130, 246, 0.8), inset 0 -2px 0 0 rgba(59, 130, 246, 0.8), 0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            : 'inset -2px 2px 0 0 transparent, inset 0 -2px 0 0 transparent';

          const baseCellStyle = isSafari
            ? {
                boxShadow: baseCellShadow,
                WebkitBoxShadow: baseCellShadow,
                transition: 'all 0.2s ease',
              }
            : {};

          const firstCellStyle = isSafari
            ? {
                boxShadow: firstCellShadow,
                WebkitBoxShadow: firstCellShadow,
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                transition: 'all 0.2s ease',
              }
            : undefined;

          const lastCellStyle = isSafari
            ? {
                boxShadow: lastCellShadow,
                WebkitBoxShadow: lastCellShadow,
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                transition: 'all 0.2s ease',
              }
            : undefined;

          const cells = [
            <TableCell
              key='server-name'
              className={cn('cursor-pointer', !isSafari && 'group-hover:rounded-l-lg')}
              style={firstCellStyle}
              onMouseEnter={() => setNameHover(data.serverName)}
              onMouseLeave={() => setNameHover('')}
              onClick={() => onCopyName(data.serverName)}
            >
              <Tooltip
                content='Copied'
                isOpen={isNameCopy === data.serverName}
                closeDelay={100}
                classNames={{
                  content: 'bg-[#F9E79F]',
                }}
              >
                <div className='flex content-center justify-between'>
                  {data.serverName}
                  <DocumentDuplicateIcon
                    width={16}
                    color='gray'
                    className={cn(nameHover !== data.serverName && 'hidden')}
                  />
                </div>
              </Tooltip>
            </TableCell>,
            <TableCell
              key='server-host'
              className='cursor-pointer'
              style={baseCellStyle}
              onMouseEnter={() => setHostHover(data.serverHost)}
              onMouseLeave={() => setHostHover('')}
              onClick={() => onCopyHost(data.serverHost)}
            >
              <Tooltip
                content='Copied'
                isOpen={isHostCopy === data.serverHost}
                closeDelay={100}
                classNames={{
                  content: 'bg-[#F9E79F]',
                }}
              >
                <div className='flex content-center justify-between'>
                  {data.serverHost}
                  <DocumentDuplicateIcon
                    width={16}
                    color='gray'
                    className={cn(hostHover !== data.serverHost && 'hidden')}
                  />
                </div>
              </Tooltip>
            </TableCell>,
            <TableCell key='cluster-tag' style={baseCellStyle}>
              {data.clusterTag}
            </TableCell>,
            <TableCell key='ram-usage' style={baseCellStyle}>
              {data.ramUsage}
            </TableCell>,
            <TableCell key='speed' style={baseCellStyle}>
              {data.networkSpeed}
            </TableCell>,
            <TableCell key='today' style={baseCellStyle}>
              {data.networkUsageToday}
            </TableCell>,
            <TableCell key='yesterday' style={baseCellStyle}>
              {data.networkUsageYesterday}
            </TableCell>,
            <TableCell key='uptime' style={baseCellStyle}>
              {data.uptime}
            </TableCell>,
            <TableCell key='service' style={baseCellStyle}>
              {data.service}
            </TableCell>,
            ...(showSslPasswords
              ? [
                  <TableCell key='ssl-password' style={baseCellStyle}>
                    {(() => {
                      const isSslEnabled = serverStats?.[data.schema.uuid]?.stats?.services?.http_proxy?.active;

                      // If cluster filter is active and server is not in selected cluster, don't show
                      if (selectedCluster !== 'all' && data.schema.clusterTag !== selectedCluster) {
                        return <span className='text-gray-400 text-sm'>-</span>;
                      }

                      // If server doesn't have SSL enabled, show "Not SSL server"
                      if (!isSslEnabled) {
                        return <span className='text-gray-400 text-sm'>Not SSL server</span>;
                      }

                      // Check if password is loading
                      if (sslPasswordLoading?.[data.schema.uuid]) {
                        return <span className='text-gray-400 text-sm'>Loading...</span>;
                      }

                      // Check if there was an error
                      if (sslPasswordErrors?.[data.schema.uuid]) {
                        return <span className='text-red-400 text-sm'>Error</span>;
                      }

                      // Check if password has been fetched
                      if (sslPasswords?.[data.schema.uuid] !== undefined) {
                        if (sslPasswords[data.schema.uuid] !== null) {
                          return <span className='font-mono text-sm'>{sslPasswords[data.schema.uuid]}</span>;
                        } else {
                          return <span className='text-gray-400 text-sm'>Not SSL server</span>;
                        }
                      }

                      // Not fetched yet
                      return <span className='text-gray-400 text-sm'>Loading...</span>;
                    })()}
                  </TableCell>,
                ]
              : []),
            <TableCell key='status' className={!isSafari ? 'group-hover:rounded-r-lg' : ''} style={lastCellStyle}>
              {data.status}
            </TableCell>,
          ];

          return (
            <TableRow
              key={index}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
              className={rowClassName}
            >
              {cells}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
